function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { Transaction } from './transaction';
/**
 * A handler's return is only an authoritative server entity if it is a real
 * object. Some endpoints (e.g. the task "mini" update) resolve with an empty
 * body (`""`) or no content; committing that as the entity would clobber the
 * store and clear the optimistic overlay, reverting the visible value to the
 * stale synced one. Treat any non-object return as "no server value — keep the
 * optimistic/merged value".
 */ const isEntity = (v)=>v != null && typeof v === 'object';
/**
 * Run `cb` AFTER the next browser paint.
 *
 * Notifications are already deferred off the mutation's call stack, but a bare
 * `setTimeout(0)` macrotask can still run BEFORE the next paint — so the
 * subscriber cascade (which includes `useSyncExternalStore`/`useCollectionitem`
 * re-renders that React runs SYNCHRONOUSLY and cannot defer) preempts the paint
 * of whatever the interaction just changed (e.g. an editor/popover closing).
 * That makes an optimistic edit feel UI-blocking: the editor visibly closes
 * only after the cascade finishes.
 *
 * `requestAnimationFrame` fires just before a paint; scheduling a macrotask
 * from inside it runs the cascade just AFTER that frame paints — so the
 * interaction (editor close) paints first and the heavy re-render work happens
 * on the next turn. Falls back to a plain macrotask where rAF is unavailable
 * (node / SSR / tests / background tabs, where there is no paint to wait for).
 */ const scheduleAfterPaint = (cb)=>{
    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(()=>setTimeout(cb, 0));
    } else {
        setTimeout(cb, 0);
    }
};
/**
 * A normalized, push-based, in-memory store for a single entity type.
 *
 * - `push()` feeds server data in (from API calls, AgGrid pages, WS refetches).
 * - `insert/update/delete` apply optimistic changes and persist via handlers.
 * - reads (`get`) overlay optimistic state on top of the synced backing store.
 * - `subscribeToId` gives O(1), per-record reactivity.
 * - `purge` frees memory, deferring removal while listeners or in-flight
 *   transactions still reference an entry.
 */ export class Collection {
    // ─── Secondary indexes ─────────────────────────────────────────────────
    /** Normalize an index fn's return to a flat key list. */ static keysOf(raw) {
        if (raw == null) return [];
        return Array.isArray(raw) ? raw : [
            raw
        ];
    }
    /**
   * Re-file `id` from `oldEntity`'s keys to `newEntity`'s keys across every
   * index. Pass `undefined` for an insert (`oldEntity`) or a delete
   * (`newEntity`). No-op when no indexes are configured.
   */ reindex(id, oldEntity, newEntity) {
        if (this.indexDefs.length === 0) return;
        for (const def of this.indexDefs){
            const map = this.indexMaps.get(def.name);
            if (oldEntity !== undefined) {
                for (const key of Collection.keysOf(def.fn(oldEntity))){
                    const set = map.get(key);
                    if (set) {
                        set.delete(id);
                        if (set.size === 0) map.delete(key);
                    }
                }
            }
            if (newEntity !== undefined) {
                for (const key of Collection.keysOf(def.fn(newEntity))){
                    let set = map.get(key);
                    if (!set) {
                        set = new Set();
                        map.set(key, set);
                    }
                    set.add(id);
                }
            }
        }
    }
    /**
   * O(result) lookup of all VISIBLE entities filed under `key` in index `name`.
   * Applies the optimistic overlay (skips optimistically-deleted ids). Returns
   * `[]` for an unknown index or empty key. Replaces `getAll().filter(...)` for
   * the indexed field — O(matches) instead of O(n).
   */ getByIndex(name, key) {
        const map = this.indexMaps.get(name);
        if (!map) return [];
        const ids = map.get(key);
        if (!ids) return [];
        const out = [];
        ids.forEach((id)=>{
            const value = this.get(id);
            if (value !== undefined) out.push(value);
        });
        return out;
    }
    // ─── Reads (O(1)) ──────────────────────────────────────────────────────
    /** Effective value: optimistic overlay over synced backing store. */ get(id) {
        const o = this.overlay.get(id);
        if (o) {
            return o.kind === 'delete' ? undefined : o.value;
        }
        return this.synced.get(id);
    }
    has(id) {
        return this.get(id) !== undefined;
    }
    /** Number of visible entities. */ size() {
        let count = this.synced.size;
        this.overlay.forEach((entry, key)=>{
            if (entry.kind === 'insert' && !this.synced.has(key)) {
                count += 1;
            } else if (entry.kind === 'delete' && this.synced.has(key)) {
                count -= 1;
            }
        });
        return count;
    }
    /** Snapshot of all visible entities. O(n) — prefer `get` for single reads. */ getAll() {
        const result = [];
        this.synced.forEach((value, key)=>{
            const o = this.overlay.get(key);
            if (o) {
                if (o.kind !== 'delete') {
                    result.push(o.value);
                }
                return;
            }
            result.push(value);
        });
        // overlay-only inserts (not yet in synced)
        this.overlay.forEach((entry, key)=>{
            if (entry.kind === 'insert' && !this.synced.has(key)) {
                result.push(entry.value);
            }
        });
        return result;
    }
    getState(id) {
        const o = this.overlay.get(id);
        if (o) {
            if (o.kind === 'insert') return 'creating';
            if (o.kind === 'update') return 'updating';
            return 'deleting';
        }
        return this.synced.has(id) ? 'synced' : undefined;
    }
    // ─── Data intake ───────────────────────────────────────────────────────
    /**
   * Feed server-shaped entities into the backing store. Deduplicates by key
   * and applies the `isNewer` staleness check. Entries with an active
   * optimistic overlay keep their overlay (visible value unchanged) while the
   * backing store is still refreshed underneath.
   *
   * `opts.mode`:
   * - `'replace'` (default) — the incoming entity wholly replaces the synced
   *   value.
   * - `'merge'` — shallow-overlay the incoming fields onto the existing synced
   *   value (`{ ...existing, ...incoming }`). Use for partial/lower-fidelity
   *   sources (a light list landing over a full detail record) so absent fields
   *   are NOT dropped. The merged result is still subject to `isNewer`.
   */ push(entities, opts = {}) {
        if (entities.length === 0) {
            return;
        }
        const merge = opts.mode === 'merge';
        // Bulk fast path: building (and later iterating) an N-entry `changed` Set
        // is pure waste on a cold load — the common case is a list/detail fetch
        // landing BEFORE any component has subscribed. Only track changed ids when
        // something is actually listening. (Per-id `idListeners` are checked per
        // entry below; `changeListeners` decide whether we track at all.)
        const track = this.changeListeners.size > 0 || this.idListeners.size > 0;
        const changed = track ? new Set() : null;
        const hasIndexes = this.indexDefs.length > 0;
        for (const incoming of entities){
            const key = this.getKey(incoming);
            const existing = this.synced.get(key);
            // In merge mode, overlay the incoming partial onto the existing record so
            // fields the lower-fidelity source omits are preserved.
            const entity = merge && existing !== undefined ? {
                ...existing,
                ...incoming
            } : incoming;
            if (existing !== undefined && !this.isNewer(entity, existing)) {
                continue;
            }
            this.synced.set(key, entity);
            if (hasIndexes) {
                this.reindex(key, existing, entity);
            }
            // NOTE: deliberately does NOT bump `syncedSeq`. A WS refetch that races
            // an in-flight mutation can carry pre-mutation (stale) state; letting it
            // win would re-introduce the very flicker we're removing. While the
            // mutation is in flight the overlay masks this value anyway; once the
            // mutation settles, the seq guard in `commitUpdate` keeps the newest
            // optimistic/server value. A WS refetch that lands AFTER settle (no
            // overlay) updates the visible value normally.
            // The visible value only changed if no overlay is masking this id.
            if (changed && !this.overlay.has(key)) {
                changed.add(key);
            }
        }
        if (changed) {
            this.notify(changed);
        }
    }
    /**
   * Remove an entity that has already been deleted on the server (WS event or
   * a confirmed delete call). Unlike `delete()`, this does NOT run the
   * `onDelete` handler — the server change is authoritative — and removes the
   * entry immediately regardless of listeners.
   */ externalDelete(id) {
        const existed = this.synced.has(id) || this.overlay.has(id);
        this.reindex(id, this.synced.get(id), undefined);
        this.synced.delete(id);
        this.overlay.delete(id);
        this.overlaySeq.delete(id);
        this.syncedSeq.delete(id);
        if (existed) {
            this.notify(new Set([
                id
            ]));
        }
    }
    // ─── Optimistic mutations ──────────────────────────────────────────────
    insert(data, opts = {}) {
        var _opts_optimistic;
        const key = this.getKey(data);
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        if (optimistic) {
            this.overlay.set(key, {
                kind: 'insert',
                value: data
            });
            this.notify(new Set([
                key
            ]));
        }
        this.notifyMutation(new Set([
            key
        ]));
        const ctx = {
            id: key,
            type: 'insert',
            modified: data,
            metadata: opts.metadata
        };
        return this.runHandler(key, this.options.onInsert, ctx, {
            onRollback: ()=>{
                if (optimistic) {
                    this.overlay.delete(key);
                    this.notify(new Set([
                        key
                    ]));
                }
            },
            onCommit: (returned)=>this.commitInsert(key, data, returned, optimistic)
        });
    }
    update(id, changes, opts = {}) {
        var _opts_optimistic;
        const original = this.get(id);
        if (original === undefined) {
            const tx = new Transaction(()=>undefined);
            tx.fail(new Error(`update: no entity for key ${String(id)}`));
            return tx;
        }
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        // Compute the optimistic overlay. Defaults to a shallow merge; a collection
        // can supply `mergeChanges` to merge nested collections element-wise (e.g.
        // custom `fields[]` by id) instead of replacing them wholesale.
        const merged = this.applyChanges(original, changes);
        // Stamp this mutation so overlapping updates to the same id stay
        // flicker-free regardless of server-response ordering.
        const seq = this.seqCounter += 1;
        if (optimistic) {
            this.overlay.set(id, {
                kind: 'update',
                value: merged
            });
            this.overlaySeq.set(id, seq);
            // Re-file `id` into its new index bucket(s) NOW, not on server commit —
            // otherwise an index-backed read (`getByIndex`, e.g. a phase swimlane's
            // task list) keeps the id in its OLD bucket until the round-trip
            // settles, even though `get(id)` already reflects the new value.
            // `reindex` only touches the specific keys `original`/`merged` derive
            // (O(1) per index), not a full index rebuild.
            this.reindex(id, original, merged);
            this.notify(new Set([
                id
            ]));
        }
        this.notifyMutation(new Set([
            id
        ]));
        const ctx = {
            id,
            type: 'update',
            changes,
            original,
            modified: merged,
            metadata: opts.metadata
        };
        return this.runHandler(id, this.options.onUpdate, ctx, {
            onRollback: ()=>this.rollbackUpdate(id, seq, optimistic),
            onCommit: (returned)=>this.commitUpdate(id, seq, changes, merged, returned)
        });
    }
    delete(id, opts = {}) {
        var _opts_optimistic;
        const original = this.get(id);
        if (original === undefined) {
            const tx = new Transaction(()=>undefined);
            tx.fail(new Error(`delete: no entity for key ${String(id)}`));
            return tx;
        }
        const optimistic = (_opts_optimistic = opts.optimistic) !== null && _opts_optimistic !== void 0 ? _opts_optimistic : true;
        if (optimistic) {
            this.overlay.set(id, {
                kind: 'delete'
            });
            this.notify(new Set([
                id
            ]));
        }
        this.notifyMutation(new Set([
            id
        ]));
        const ctx = {
            id,
            type: 'delete',
            original,
            metadata: opts.metadata
        };
        return this.runHandler(id, this.options.onDelete, ctx, {
            onRollback: ()=>{
                if (optimistic) {
                    this.overlay.delete(id);
                    this.notify(new Set([
                        id
                    ]));
                }
            },
            onCommit: ()=>{
                this.reindex(id, this.synced.get(id), undefined);
                this.synced.delete(id);
                this.overlay.delete(id);
                this.notify(new Set([
                    id
                ]));
            }
        });
    }
    /**
   * Apply multiple optimistic mutations as ONE transaction, persisted by a
   * single `mutationFn` (e.g. a bulk endpoint). All overlays are applied
   * immediately; on failure they all roll back together.
   *
   * Use for bulk edits, multi-task dependency updates, or any change that the
   * backend accepts as one atomic call. `mutationFn` may return the
   * server-confirmed entities to commit; otherwise the optimistic values are
   * committed.
   */ batch(build, mutationFn) {
        const mutations = [];
        const affected = new Set();
        // Snapshot prior overlay per id so rollback is exact.
        const prevOverlay = new Map();
        const setOverlay = (id, entry)=>{
            if (!prevOverlay.has(id)) {
                prevOverlay.set(id, this.overlay.get(id));
            }
            this.overlay.set(id, entry);
            affected.add(id);
        };
        const recorder = {
            insert: (data)=>{
                const id = this.getKey(data);
                setOverlay(id, {
                    kind: 'insert',
                    value: data
                });
                mutations.push({
                    type: 'insert',
                    id,
                    modified: data
                });
            },
            update: (id, changes)=>{
                const original = this.get(id);
                if (original === undefined) {
                    return;
                }
                const merged = this.applyChanges(original, changes);
                setOverlay(id, {
                    kind: 'update',
                    value: merged
                });
                mutations.push({
                    type: 'update',
                    id,
                    changes,
                    original,
                    modified: merged
                });
            },
            remove: (id)=>{
                const original = this.get(id);
                if (original === undefined) {
                    return;
                }
                setOverlay(id, {
                    kind: 'delete'
                });
                mutations.push({
                    type: 'delete',
                    id,
                    original
                });
            }
        };
        build(recorder);
        this.notify(affected);
        this.notifyMutation(affected);
        const rollback = ()=>{
            prevOverlay.forEach((entry, id)=>{
                if (entry === undefined) {
                    this.overlay.delete(id);
                } else {
                    this.overlay.set(id, entry);
                }
            });
            this.notify(affected);
        };
        const tx = new Transaction(rollback);
        affected.forEach((id)=>this.trackTx(id, tx));
        const untrackAll = ()=>{
            affected.forEach((id)=>this.untrackTx(id, tx));
        };
        if (mutations.length === 0) {
            untrackAll();
            tx.complete();
            return tx;
        }
        tx.markPersisting();
        Promise.resolve().then(()=>mutationFn({
                mutations
            })).then((returned)=>{
            if (tx.state === 'failed') {
                untrackAll();
                return;
            }
            this.commitBatch(mutations, returned !== null && returned !== void 0 ? returned : undefined);
            untrackAll();
            tx.complete();
        }).catch((error)=>{
            if (tx.state !== 'failed') {
                rollback();
                untrackAll();
                tx.fail(error);
            } else {
                untrackAll();
            }
        });
        return tx;
    }
    // ─── Memory management ─────────────────────────────────────────────────
    /** Remove all entries matching the predicate (deferred where referenced). */ purge(predicate) {
        const candidates = [];
        this.synced.forEach((value, key)=>{
            if (predicate(value)) {
                candidates.push(key);
            }
        });
        this.overlay.forEach((entry, key)=>{
            if (entry.kind !== 'delete' && !this.synced.has(key) && predicate(entry.value)) {
                candidates.push(key);
            }
        });
        candidates.forEach((key)=>this.requestPurge(key));
    }
    purgeById(ids) {
        ids.forEach((id)=>this.requestPurge(id));
    }
    /** Wipe everything. Ignores listeners and in-flight transactions. */ clear() {
        const changed = new Set([
            ...this.synced.keys(),
            ...this.overlay.keys()
        ]);
        this.synced.clear();
        this.overlay.clear();
        this.overlaySeq.clear();
        this.syncedSeq.clear();
        this.pendingPurge.clear();
        this.indexMaps.forEach((map)=>map.clear());
        this.notify(changed);
    }
    // ─── Subscriptions ─────────────────────────────────────────────────────
    /** Subscribe to a single id. Fires only when that id's value changes. */ subscribeToId(id, listener) {
        let set = this.idListeners.get(id);
        if (!set) {
            set = new Set();
            this.idListeners.set(id, set);
        }
        set.add(listener);
        return ()=>{
            const s = this.idListeners.get(id);
            if (!s) return;
            s.delete(listener);
            if (s.size === 0) {
                this.idListeners.delete(id);
            }
            this.maybePurge(id);
        };
    }
    /** Subscribe to all changes; receives the set of changed ids. */ subscribeToChanges(listener) {
        this.changeListeners.add(listener);
        return ()=>{
            this.changeListeners.delete(listener);
        };
    }
    /**
   * Subscribe to optimistic mutations only (`insert`/`update`/`delete`). Does
   * NOT fire for `push` (hydration / WS refetch). See `mutationListeners`.
   */ subscribeToMutations(listener) {
        this.mutationListeners.add(listener);
        return ()=>{
            this.mutationListeners.delete(listener);
        };
    }
    // ─── Internals ─────────────────────────────────────────────────────────
    notify(changedIds) {
        if (changedIds.size === 0) {
            return;
        }
        changedIds.forEach((id)=>this.pendingChangeIds.add(id));
        this.scheduleFlush();
    }
    /** Coalesce + flush notifications on a macrotask (see field docs). */ scheduleFlush() {
        if (this.flushScheduled) {
            return;
        }
        this.flushScheduled = true;
        // Run the subscriber cascade AFTER the next paint (see `scheduleAfterPaint`)
        // so the interaction that triggered the mutation — e.g. an editor/popover
        // closing — paints first instead of being blocked behind the cascade.
        scheduleAfterPaint(()=>this.flushNotifications());
    }
    flushNotifications() {
        this.flushScheduled = false;
        if (this.pendingChangeIds.size > 0) {
            const changedIds = this.pendingChangeIds;
            this.pendingChangeIds = new Set();
            changedIds.forEach((id)=>{
                const set = this.idListeners.get(id);
                if (set) {
                    set.forEach((fn)=>fn());
                }
            });
            this.changeListeners.forEach((fn)=>fn(changedIds));
        }
        if (this.pendingMutationIds.size > 0 && this.mutationListeners.size > 0) {
            const mutatedIds = this.pendingMutationIds;
            this.pendingMutationIds = new Set();
            this.mutationListeners.forEach((fn)=>fn(mutatedIds));
        } else {
            this.pendingMutationIds = new Set();
        }
    }
    /** Notify mutation-only listeners (optimistic insert/update/delete). */ notifyMutation(changedIds) {
        if (changedIds.size === 0 || this.mutationListeners.size === 0) {
            return;
        }
        changedIds.forEach((id)=>this.pendingMutationIds.add(id));
        this.scheduleFlush();
    }
    trackTx(id, tx) {
        let set = this.pendingTx.get(id);
        if (!set) {
            set = new Set();
            this.pendingTx.set(id, set);
        }
        set.add(tx);
    }
    untrackTx(id, tx) {
        const set = this.pendingTx.get(id);
        if (!set) return;
        set.delete(tx);
        if (set.size === 0) {
            this.pendingTx.delete(id);
        }
        this.maybePurge(id);
    }
    hasListeners(id) {
        const set = this.idListeners.get(id);
        return !!set && set.size > 0;
    }
    hasPendingTx(id) {
        const set = this.pendingTx.get(id);
        return !!set && set.size > 0;
    }
    /** Mark an id for purge; remove immediately if unreferenced. */ requestPurge(id) {
        this.pendingPurge.add(id);
        this.maybePurge(id);
    }
    /** Remove an id if it is marked for purge and no longer referenced. */ maybePurge(id) {
        if (!this.pendingPurge.has(id)) {
            return;
        }
        if (this.hasListeners(id) || this.hasPendingTx(id)) {
            return;
        }
        this.pendingPurge.delete(id);
        const existed = this.synced.has(id) || this.overlay.has(id);
        this.reindex(id, this.synced.get(id), undefined);
        this.synced.delete(id);
        this.overlay.delete(id);
        this.overlaySeq.delete(id);
        this.syncedSeq.delete(id);
        if (existed) {
            this.notify(new Set([
                id
            ]));
        }
    }
    /**
   * Runs an operation handler with optimistic lifecycle management. The
   * handler persists to the backend; on success `onCommit` applies the
   * confirmed state, on failure `onRollback` reverts the optimistic overlay.
   */ runHandler(id, handler, ctx, hooks) {
        const tx = new Transaction(()=>hooks.onRollback());
        this.trackTx(id, tx);
        if (!handler) {
            // No persistence: commit the optimistic value immediately.
            hooks.onCommit(undefined);
            // untrack before completing so any deferred purge fires before
            // `isPersisted` awaiters observe the committed state.
            this.untrackTx(id, tx);
            tx.complete();
            return tx;
        }
        tx.markPersisting();
        Promise.resolve().then(()=>handler(ctx)).then((returned)=>{
            if (tx.state === 'failed') {
                this.untrackTx(id, tx); // rolled back during flight
                return;
            }
            hooks.onCommit(returned !== null && returned !== void 0 ? returned : undefined);
            this.untrackTx(id, tx);
            tx.complete();
        }).catch((error)=>{
            if (tx.state !== 'failed') {
                hooks.onRollback();
                this.untrackTx(id, tx);
                tx.fail(error);
            } else {
                this.untrackTx(id, tx);
            }
        });
        return tx;
    }
    commitInsert(tempKey, optimisticValue, returned, optimistic) {
        const changed = new Set();
        if (optimistic) {
            this.overlay.delete(tempKey);
            changed.add(tempKey);
        }
        if (isEntity(returned)) {
            const realKey = this.getKey(returned);
            this.reindex(realKey, this.synced.get(realKey), returned);
            this.synced.set(realKey, returned);
            changed.add(realKey);
            // id swap: drop the temp entry entirely
            if (realKey !== tempKey) {
                this.reindex(tempKey, this.synced.get(tempKey), undefined);
                this.synced.delete(tempKey);
                this.overlay.delete(tempKey);
            }
        } else {
            // commit the optimistic value under its key
            this.reindex(tempKey, this.synced.get(tempKey), optimisticValue);
            this.synced.set(tempKey, optimisticValue);
            changed.add(tempKey);
        }
        this.notify(changed);
    }
    commitBatch(mutations, returned) {
        const changed = new Set();
        if (returned) {
            returned.forEach((entity)=>{
                if (!isEntity(entity)) {
                    return;
                }
                const key = this.getKey(entity);
                this.reindex(key, this.synced.get(key), entity);
                this.synced.set(key, entity);
                changed.add(key);
            });
        }
        mutations.forEach((m)=>{
            this.overlay.delete(m.id);
            changed.add(m.id);
            if (m.type === 'delete') {
                this.reindex(m.id, this.synced.get(m.id), undefined);
                this.synced.delete(m.id);
            } else if (!returned && m.modified !== undefined) {
                // No server payload: commit the optimistic value.
                this.reindex(m.id, this.synced.get(m.id), m.modified);
                this.synced.set(m.id, m.modified);
            }
        });
        this.notify(changed);
    }
    /**
   * Commit one optimistic update. `seq` orders overlapping updates to the same
   * id so that out-of-order server responses cannot flicker the visible value:
   *
   * - The synced backing store only accepts this value if `seq` is at least as
   *   new as the last value written there (a late response from an older
   *   update never clobbers a newer one).
   * - The optimistic overlay is cleared only when THIS commit corresponds to
   *   the latest optimistic intent (`overlaySeq === seq`). While newer updates
   *   are still in flight, the overlay keeps masking the backing store, so the
   *   user keeps seeing their most recent change until it too settles.
   */ /** Optimistic overlay value: custom `mergeChanges` or a shallow spread. */ applyChanges(original, changes) {
        return this.options.mergeChanges ? this.options.mergeChanges(original, changes) : {
            ...original,
            ...changes
        };
    }
    commitUpdate(id, seq, changes, merged, returned) {
        var _this_syncedSeq_get;
        const hasServerValue = isEntity(returned);
        // Void responses must re-merge the original patch onto the latest synced
        // record. Committing the pre-request `merged` snapshot would clobber
        // newer fields that arrived via push/socket while the request was in flight.
        const latestSynced = this.synced.get(id);
        const value = hasServerValue ? returned : latestSynced != null ? this.applyChanges(latestSynced, changes) : merged;
        const key = hasServerValue ? this.getKey(returned) : id;
        if (seq >= ((_this_syncedSeq_get = this.syncedSeq.get(key)) !== null && _this_syncedSeq_get !== void 0 ? _this_syncedSeq_get : 0)) {
            // Reindex from `merged` (the value `update()`'s optimistic apply already
            // re-filed the index to under `id`), not the pre-transaction synced
            // value — the index has been sitting on `merged`'s buckets since the
            // optimistic apply. Reindexing from the OLD synced value here would
            // leave a leaked bucket entry in the rare case the server's confirmed
            // value's index key differs from what was optimistically merged (Set
            // add/delete are idempotent, so the common same-key case is unaffected).
            if (key !== id) {
                // Server returned a different key than the one being updated (id
                // swap) — drop the optimistic entry filed under `id` before filing
                // the confirmed value under `key`, or `id` leaks in the index forever.
                this.reindex(id, merged, undefined);
            }
            this.reindex(key, merged, value);
            this.synced.set(key, value);
            this.syncedSeq.set(key, seq);
        }
        if (this.overlaySeq.get(id) === seq) {
            this.overlay.delete(id);
            this.overlaySeq.delete(id);
        }
        this.notify(key === id ? new Set([
            id
        ]) : new Set([
            id,
            key
        ]));
    }
    /**
   * Roll back one optimistic update. Only the LATEST optimistic intent owns the
   * overlay, so an older mutation failing while a newer one is still in flight
   * must NOT drop the overlay — the newer change stays visible. When the latest
   * mutation is the one rolling back, the overlay is removed and the (last
   * committed) synced value becomes visible again.
   */ rollbackUpdate(id, seq, optimistic) {
        if (!optimistic) {
            return;
        }
        if (this.overlaySeq.get(id) === seq) {
            // Re-file back OUT of the optimistic bucket the matching `update()`
            // call filed `id` into — mirrors that call's `reindex(id, original,
            // merged)`. Read the about-to-be-reverted value before deleting the
            // overlay, and the post-rollback value after, so the reindex uses the
            // exact before/after pair (falls back to the synced value, or a
            // lower-seq overlay if one still exists underneath).
            const before = this.get(id);
            this.overlay.delete(id);
            this.overlaySeq.delete(id);
            this.reindex(id, before, this.get(id));
            this.notify(new Set([
                id
            ]));
        }
    }
    constructor(options){
        var _options_isNewer;
        _define_property(this, "id", void 0);
        _define_property(this, "getKey", void 0);
        _define_property(this, "isNewer", void 0);
        _define_property(this, "options", void 0);
        /** Server-confirmed state. */ _define_property(this, "synced", new Map());
        /** Optimistic overlay for ids with in-flight mutations. */ _define_property(this, "overlay", new Map());
        /** Per-id listeners (reference counted). */ _define_property(this, "idListeners", new Map());
        /** Global change listeners; receive the set of changed ids. */ _define_property(this, "changeListeners", new Set());
        /**
   * Mutation listeners — fire ONLY for optimistic `insert`/`update`/`delete`
   * (a user-driven write), NOT for `push` (server hydration / WS refetch). Use
   * when a consumer must react to genuine edits but must NOT be woken by bulk
   * data loads (e.g. the kanban board re-laying out a single edited card —
   * subscribing to every change would turn a bulk list hydration into one
   * server request per task).
   */ _define_property(this, "mutationListeners", new Set());
        /**
   * Notifications are coalesced and flushed on a macrotask, NOT synchronously.
   * The backing store / overlay are updated synchronously (so `get(id)` returns
   * the new value immediately — optimistic edits are instant), but the
   * subscriber cascade (per-id listeners, `subscribeToChanges`,
   * `subscribeToMutations` — which drive React re-renders, O(n) selects, and the
   * gantt sync) runs off the mutation's call stack. This keeps a `mutate()` from
   * blocking the UI thread (e.g. an editor blur) while the cascade runs, and
   * batches rapid successive edits into a single flush. Ordering of the
   * authoritative state (seq/overlay/commit) is unaffected — only notification
   * timing is deferred.
   */ _define_property(this, "pendingChangeIds", new Set());
        _define_property(this, "pendingMutationIds", new Set());
        _define_property(this, "flushScheduled", false);
        /** In-flight transactions per id. */ _define_property(this, "pendingTx", new Map());
        /** Ids marked for removal once listeners + transactions clear. */ _define_property(this, "pendingPurge", new Set());
        /**
   * Monotonic sequence stamped on each optimistic mutation. Used to keep
   * overlapping updates to the same id flicker-free: out-of-order server
   * responses must not expose an older value or clear a newer optimistic
   * overlay.
   */ _define_property(this, "seqCounter", 0);
        /** Seq of the latest optimistic overlay per id (the newest user intent). */ _define_property(this, "overlaySeq", new Map());
        /** Seq of the latest value written into the synced backing store per id. */ _define_property(this, "syncedSeq", new Map());
        /**
   * Secondary indexes: `indexName → (indexKey → Set<entityId>)`. Maintained
   * over the SYNCED store so `getByIndex` is O(result). Empty when no indexes
   * are configured (zero overhead on the hot ingest path).
   */ _define_property(this, "indexDefs", []);
        _define_property(this, "indexMaps", new Map());
        this.id = options.id;
        this.getKey = options.getKey;
        this.isNewer = (_options_isNewer = options.isNewer) !== null && _options_isNewer !== void 0 ? _options_isNewer : ()=>true;
        this.options = options;
        if (options.indexes) {
            for (const [name, fn] of Object.entries(options.indexes)){
                this.indexDefs.push({
                    name,
                    fn
                });
                this.indexMaps.set(name, new Map());
            }
        }
    }
}
export function createCollection(options) {
    return new Collection(options);
}
